import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DateFilter } from '@/components/DateFilter';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Download, TrendingUp, TrendingDown, ChevronDown, ChevronRight, BarChart3, AlertTriangle, Award, Info } from 'lucide-react';
import api from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import BranchLink from '@/components/BranchLink';
import { exportToCsv, slug } from '@/lib/exportCsv';

const fmtSR = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '—';
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const cellTone = (v, highlight) => {
  if (v === 0 || v == null) return 'text-muted-foreground/60';
  if (highlight === 'profit') return v >= 0 ? 'text-success font-semibold' : 'text-error font-semibold';
  if (highlight === 'loss') return 'text-error font-semibold';
  if (highlight === 'revenue') return 'text-info font-semibold';
  if (highlight === 'cogs' || highlight === 'expense') return 'text-warning font-semibold';
  return v < 0 ? 'text-error' : 'text-foreground';
};

// Bilingual (EN/AR) explanations for the inter-branch adjustment rows so branch
// managers understand how cross-branch spending flows into their Net.
const IB_TOOLTIPS = {
  ib_receivable: {
    en: 'Money this branch paid on behalf of OTHER branches. It is added back (＋) to this branch\u2019s Net Profit because the cost really belongs to them, not you.',
    ar: '\u0627\u0644\u0645\u0628\u0627\u0644\u063a \u0627\u0644\u062a\u064a \u062f\u0641\u0639\u0647\u0627 \u0647\u0630\u0627 \u0627\u0644\u0641\u0631\u0639 \u0646\u064a\u0627\u0628\u0629\u064b \u0639\u0646 \u0641\u0631\u0648\u0639 \u0623\u062e\u0631\u0649. \u062a\u064f\u0636\u0627\u0641 (\u002b) \u0625\u0644\u0649 \u0635\u0627\u0641\u064a \u0631\u0628\u062d \u0647\u0630\u0627 \u0627\u0644\u0641\u0631\u0639 \u0644\u0623\u0646 \u0627\u0644\u062a\u0643\u0644\u0641\u0629 \u062a\u062e\u0635\u0651\u0647\u0645 \u0648\u0644\u064a\u0633 \u0623\u0646\u062a.',
  },
  ib_payable: {
    en: 'Money OTHER branches paid on behalf of this branch. It is charged (\u2212) to this branch\u2019s Net Profit because the cost really belongs to you.',
    ar: '\u0627\u0644\u0645\u0628\u0627\u0644\u063a \u0627\u0644\u062a\u064a \u062f\u0641\u0639\u062a\u0647\u0627 \u0641\u0631\u0648\u0639 \u0623\u062e\u0631\u0649 \u0646\u064a\u0627\u0628\u0629\u064b \u0639\u0646 \u0647\u0630\u0627 \u0627\u0644\u0641\u0631\u0639. \u062a\u064f\u062e\u0635\u0645 (\u2212) \u0645\u0646 \u0635\u0627\u0641\u064a \u0631\u0628\u062d \u0647\u0630\u0627 \u0627\u0644\u0641\u0631\u0639 \u0644\u0623\u0646 \u0627\u0644\u062a\u0643\u0644\u0641\u0629 \u062a\u062e\u0635\u0651\u0643.',
  },
  ib_net: {
    en: 'Net inter-branch adjustment = (I Paid for Others) \u2212 (Others Paid for Me). Cash settlements do NOT change this \u2014 only real spending does.',
    ar: '\u0635\u0627\u0641\u064a \u062a\u0633\u0648\u064a\u0629 \u0627\u0644\u0641\u0631\u0648\u0639 = (\u062f\u0641\u0639\u062a \u0644\u0644\u0622\u062e\u0631\u064a\u0646) \u2212 (\u062f\u0641\u0639 \u0627\u0644\u0622\u062e\u0631\u0648\u0646 \u0644\u064a). \u0627\u0644\u062a\u0633\u0648\u064a\u0627\u062a \u0627\u0644\u0646\u0642\u062f\u064a\u0629 \u0644\u0627 \u062a\u063a\u064a\u0651\u0631 \u0647\u0630\u0627 \u2014 \u0641\u0642\u0637 \u0627\u0644\u0625\u0646\u0641\u0627\u0642 \u0627\u0644\u0641\u0639\u0644\u064a.',
  },
};

export default function MultiBranchPnLMatrix() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: 'month' });
  const [showPct, setShowPct] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const NON_DRILLABLE = useMemo(() => new Set(['gross_profit', 'net_profit', 'credit_remaining']), []);

  const openDrill = async (rowKey, rowLabel, branchId, branchName) => {
    if (NON_DRILLABLE.has(rowKey)) return;
    setDrill({ rowKey, rowLabel, branchId, branchName });
    setDrillLoading(true);
    setDrillData(null);
    try {
      const params = { row_key: rowKey };
      if (branchId) params.branch_id = branchId;
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      const r = await api.get('/reports/multi-branch-pnl-matrix/drilldown', { params });
      setDrillData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load details');
    } finally {
      setDrillLoading(false);
    }
  };

  const exportDrill = () => {
    if (!drillData?.items?.length) return;
    const period = drillData.period ? `${drillData.period.start_date}_to_${drillData.period.end_date}` : '';
    exportToCsv(
      `${slug(drill?.rowLabel)}_${slug(drill?.branchName)}_${period}.csv`,
      drillData.items,
      [
        { key: 'date', label: 'Date' },
        { key: 'description', label: 'Description' },
        { key: 'name', label: 'Name / Ref' },
        { key: 'category', label: 'Category' },
        { key: 'branch', label: 'Branch' },
        { key: 'amount', label: 'Amount', format: (v) => Number(v || 0).toFixed(2) },
      ],
    );
    toast.success(`Exported ${drillData.items.length} line items to CSV`);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      const r = await api.get('/reports/multi-branch-pnl-matrix', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load matrix');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [dateFilter.start, dateFilter.end]);

  // No-Wait UI: refresh instantly when any cross-branch txn / settlement fires
  // the shared `balance_updated` event — so this page, Branch Summary and the
  // Inter-Branch Matrix all update in real-time together.
  useWebSocket((msg) => { if (msg?.type === 'balance_updated') fetchData(); });

  const branches = data?.branches || [];

  // Derive per-branch insights: revenue, net profit, net margin %.
  // Used to surface the "best" and "worst" performer at the top.
  const insights = useMemo(() => {
    if (!data) return null;
    const gross = data.sections.find(s => s.title === 'Sales')?.rows?.find(r => r.key === 'gross_sales');
    const net = data.sections.find(s => s.title === 'Net Profit/Loss')?.rows?.find(r => r.key === 'net_profit');
    if (!gross || !net) return null;
    // Include ALL branches (even Unassigned) so the alert still renders
    // on tenants where most activity sits in the Unassigned bucket.
    const stats = branches
      .map(b => {
        const rev = gross.values[b.id] || 0;
        const profit = net.values[b.id] || 0;
        const margin = rev > 0 ? (profit / rev) * 100 : (profit < 0 ? -999 : 0);
        return { id: b.id, name: b.name, revenue: rev, profit, margin };
      })
      .filter(s => s.revenue > 0 || s.profit !== 0);
    if (stats.length === 0) return null;
    const sorted = [...stats].sort((a, b) => a.margin - b.margin);
    const worst = sorted[0];
    const best = sorted[sorted.length - 1];
    const opexRow = data.sections.find(s => s.title === 'Operating Expenses')?.rows?.find(r => r.key === 'total_opex');
    const withOpex = opexRow ? stats.map(s => ({
      ...s,
      opex: opexRow.values[s.id] || 0,
      opexRatio: s.revenue > 0 ? ((opexRow.values[s.id] || 0) / s.revenue) * 100 : null,
    })).filter(s => s.opexRatio != null) : [];
    const highSpender = withOpex.length ? [...withOpex].sort((a, b) => b.opexRatio - a.opexRatio)[0] : null;
    return { worst, best, highSpender, totalBranches: stats.length, hasMultiple: stats.length >= 2 };
  }, [data, branches]);

  // Only highlight a "worst" column when there are ≥2 branches to compare.
  const highlightWorstBranchId = insights?.hasMultiple ? insights?.worst?.id : null;

  const exportExcel = () => {
    if (!data) return;
    const rows = [['Section', 'Line Item', ...branches.map(b => b.name), 'Total', 'Margin %']];
    for (const s of data.sections) {
      for (const r of s.rows) {
        rows.push([
          s.title, r.label,
          ...branches.map(b => r.values[b.id] || 0),
          r.values.__total__ || 0,
          r.pct_of_revenue?.__total__ ?? '',
        ]);
      }
    }
    const csv = rows.map(r => r.map(c => {
      const s = String(c ?? '');
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `multi-branch-pnl-${data.period?.start_date}_to_${data.period?.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Exported to CSV');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-[1700px] mx-auto" data-testid="multi-branch-pnl-matrix-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50">
              <BarChart3 size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">
                Multi-Branch P&L Matrix
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">Pivot view: branches across, line items down — with margin %</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DateFilter onFilterChange={setDateFilter} />
            <Button variant={showPct ? 'default' : 'outline'} size="sm" onClick={() => setShowPct(!showPct)} data-testid="toggle-pct-btn">
              <TrendingUp size={14} className="mr-1" /> {showPct ? 'Hide %' : 'Show %'}
            </Button>
            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-btn">
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Insights Strip — auto-highlights worst and best branch */}
        {insights && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="insights-strip">
            {/* Worst margin branch (loss alert) */}
            <Card className={`border-2 ${insights.worst.margin < 0 ? 'border-error/40 bg-error/5' : 'border-warning/30 bg-warning/5'}`} data-testid="worst-branch-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${insights.worst.margin < 0 ? 'bg-error/15 text-error' : 'bg-warning/15 text-warning'}`}>
                    <AlertTriangle size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {!insights.hasMultiple
                        ? (insights.worst.margin < 0 ? 'Branch Losing Money' : 'Branch Performance')
                        : (insights.worst.margin < 0 ? 'Losing Branch' : 'Lowest Margin')}
                    </p>
                    <p className="text-lg font-bold font-outfit text-foreground truncate" data-testid="worst-branch-name">{insights.worst.name}</p>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className={`text-2xl font-bold tabular-nums ${insights.worst.margin < 0 ? 'text-error' : 'text-warning'}`}>
                        {isFinite(insights.worst.margin) ? `${insights.worst.margin.toFixed(1)}%` : '—'}
                      </span>
                      <span className="text-xs text-muted-foreground">margin</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Rev {fmtSR(insights.worst.revenue)} · Net <span className={insights.worst.profit < 0 ? 'text-error font-semibold' : 'text-success font-semibold'}>{fmtSR(insights.worst.profit)}</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Highest expense ratio */}
            {insights.hasMultiple && insights.highSpender && insights.highSpender.id !== insights.worst.id && (
              <Card className="border-2 border-warning/30 bg-warning/5" data-testid="high-spender-card">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-warning/15 text-warning flex items-center justify-center shrink-0">
                      <TrendingDown size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Highest OpEx Ratio</p>
                      <p className="text-lg font-bold font-outfit text-foreground truncate">{insights.highSpender.name}</p>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-2xl font-bold tabular-nums text-warning">{insights.highSpender.opexRatio?.toFixed(1)}%</span>
                        <span className="text-xs text-muted-foreground">of revenue on OpEx</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        OpEx {fmtSR(insights.highSpender.opex)} · Rev {fmtSR(insights.highSpender.revenue)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Best branch */}
            {insights.hasMultiple && insights.best && insights.best.id !== insights.worst.id && (
              <Card className="border-2 border-success/30 bg-success/5" data-testid="best-branch-card">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-success/15 text-success flex items-center justify-center shrink-0">
                      <Award size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Top Performer</p>
                      <p className="text-lg font-bold font-outfit text-foreground truncate">{insights.best.name}</p>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-2xl font-bold tabular-nums text-success">{insights.best.margin.toFixed(1)}%</span>
                        <span className="text-xs text-muted-foreground">margin</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Rev {fmtSR(insights.best.revenue)} · Net <span className="text-success font-semibold">{fmtSR(insights.best.profit)}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Matrix */}
        <Card className="overflow-hidden border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !data ? (
              <div className="p-12 text-center text-sm text-muted-foreground">No data</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" data-testid="pnl-matrix-table">
                  <thead className="sticky top-0 z-10 bg-gradient-to-b from-muted/80 to-muted/60 backdrop-blur-sm">
                    <tr>
                      <th className="text-left p-3 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground border-b border-border sticky left-0 bg-muted z-20 min-w-[220px]">
                        Line Item
                      </th>
                      {branches.map((b) => {
                        const netRow = data?.sections?.find(s => s.title === 'Net Profit/Loss')?.rows?.find(r => r.key === 'net_profit');
                        const grossRow = data?.sections?.find(s => s.title === 'Sales')?.rows?.find(r => r.key === 'gross_sales');
                        const stat = netRow?.pct_of_revenue?.[b.id];
                        const branchRevenue = grossRow?.values?.[b.id] || 0;
                        const showStat = insights?.hasMultiple && branchRevenue > 0 && stat != null;
                        const isWorstCol = b.id === highlightWorstBranchId;
                        return (
                          <th key={b.id} className={`text-right p-3 font-semibold uppercase tracking-wider text-[10px] border-b border-border min-w-[110px] ${isWorstCol ? 'bg-error/15 text-error' : 'text-foreground'}`} data-testid={`col-${b.id}`}>
                            {isWorstCol && <AlertTriangle size={10} className="inline mr-1 -mt-0.5" />}
                            <BranchLink branchId={b.id} name={b.name} />
                            {showStat && (
                              <span className={`block text-[9px] font-medium normal-case tracking-normal leading-none mt-0.5 ${stat < 0 ? 'text-error' : stat < 10 ? 'text-warning' : 'text-success'}`}>
                                {stat > 0 ? '+' : ''}{stat}% margin
                              </span>
                            )}
                          </th>
                        );
                      })}
                      <th className="text-right p-3 font-semibold uppercase tracking-wider text-[10px] text-foreground border-b border-border min-w-[110px] bg-primary/5">
                        Total
                      </th>
                      <th className="text-right p-3 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground border-b border-border min-w-[80px]">
                        % Rev
                      </th>
                    </tr>
                  </thead>
                    {data.sections.map((section) => {
                      const sKey = section.title;
                      const isCollapsed = collapsed[sKey];
                      return (
                        <tbody key={sKey}>
                          <tr className="bg-muted/40 border-y border-border">
                            <td colSpan={branches.length + 3} className="p-2.5 pl-3 font-bold text-xs uppercase tracking-wider text-foreground sticky left-0 bg-muted/40">
                              <button onClick={() => setCollapsed(p => ({ ...p, [sKey]: !p[sKey] }))} className="flex items-center gap-1.5 hover:text-primary transition-colors" data-testid={`section-${sKey}`}>
                                {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                {section.title}
                              </button>
                            </td>
                          </tr>
                          {!isCollapsed && section.rows.map((row) => (
                            <tr key={row.key} className={`border-b border-border/40 hover:bg-muted/20 ${row.bold ? 'bg-primary/5' : ''}`} data-testid={`row-${row.key}`}>
                              <td className={`p-2 sticky left-0 z-[5] ${row.bold ? 'bg-primary/10 font-bold' : 'bg-background'} ${row.indent ? 'pl-7' : 'pl-3'} ${row.italic ? 'italic text-muted-foreground' : 'text-foreground'}`}>
                                {IB_TOOLTIPS[row.key] ? (
                                  <span className="inline-flex items-center gap-1">
                                    {row.label}
                                    <TooltipProvider delayDuration={100}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button type="button" className="text-muted-foreground hover:text-primary" data-testid={`ib-tooltip-${row.key}`} aria-label="explanation">
                                            <Info size={13} />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed">
                                          <p>{IB_TOOLTIPS[row.key].en}</p>
                                          <p dir="rtl" className="mt-1 opacity-90">{IB_TOOLTIPS[row.key].ar}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </span>
                                ) : row.label}
                              </td>
                              {branches.map((b) => {
                                const v = row.values[b.id] || 0;
                                const pct = row.pct_share?.[b.id] || 0;
                                const isWorst = b.id === highlightWorstBranchId;
                                const drillable = !NON_DRILLABLE.has(row.key) && v !== 0;
                                return (
                                  <td
                                    key={b.id}
                                    onClick={drillable ? () => openDrill(row.key, row.label, b.id, b.name) : undefined}
                                    className={`p-2 text-right tabular-nums ${cellTone(v, row.highlight)} ${isWorst ? 'bg-error/5' : ''} ${drillable ? 'cursor-pointer hover:bg-primary/10 hover:underline decoration-dotted underline-offset-2' : ''}`}
                                    data-testid={drillable ? `drill-${row.key}-${b.id}` : undefined}
                                    title={drillable ? 'Click to see line items' : undefined}
                                  >
                                    <div className="leading-tight">{fmtSR(v)}</div>
                                    {showPct && v !== 0 && pct > 0 && (
                                      <div className="text-[9px] text-muted-foreground/70 leading-none mt-0.5">
                                        {pct}%
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                              <td
                                onClick={(!NON_DRILLABLE.has(row.key) && row.values.__total__) ? () => openDrill(row.key, row.label, '__total__', 'All Branches') : undefined}
                                className={`p-2 text-right tabular-nums font-semibold bg-primary/5 ${cellTone(row.values.__total__, row.highlight)} ${(!NON_DRILLABLE.has(row.key) && row.values.__total__) ? 'cursor-pointer hover:bg-primary/15 hover:underline decoration-dotted underline-offset-2' : ''}`}
                                data-testid={(!NON_DRILLABLE.has(row.key) && row.values.__total__) ? `drill-${row.key}-total` : undefined}
                              >
                                {fmtSR(row.values.__total__)}
                              </td>
                              <td className="p-2 text-right tabular-nums text-xs text-muted-foreground">
                                {row.pct_of_revenue?.__total__ != null && row.pct_of_revenue.__total__ !== 0
                                  ? `${row.pct_of_revenue.__total__}%`
                                  : <span className="text-muted-foreground/40">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      );
                    })}
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Drill-down dialog (iter754) */}
        <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
          <DialogContent className="max-w-2xl" data-testid="drill-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit text-base">
                {drill?.rowLabel} · {drill?.branchName}
              </DialogTitle>
            </DialogHeader>
            {drillLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Loading line items…</p>
            ) : !drillData || drillData.items.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="drill-empty">No line items for this cell.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs px-1">
                  <span className="text-muted-foreground">{drillData.count} item{drillData.count !== 1 ? 's' : ''}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-bold tabular-nums">Total: {fmtSR(drillData.total)}</span>
                    <Button size="sm" variant="outline" onClick={exportDrill} className="h-7 text-xs" data-testid="drill-export-btn">
                      <Download size={12} className="mr-1" /> Export CSV
                    </Button>
                  </div>
                </div>
                <div className="max-h-[55dvh] overflow-y-auto border border-border rounded-lg">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                          <th className="p-2">Date</th>
                          <th className="p-2">Description</th>
                          <th className="p-2">Name / Ref</th>
                          <th className="p-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drillData.items.map((it, i) => (
                          <tr key={`${it.id}-${i}`} className="border-t border-border/50 hover:bg-muted/30" data-testid={`drill-item-${i}`}>
                            <td className="p-2 whitespace-nowrap text-muted-foreground">{it.date}</td>
                            <td className="p-2 max-w-[200px] truncate" title={it.description}>{it.description || '—'}</td>
                            <td className="p-2 max-w-[160px] truncate" title={it.name}>
                              {it.name}
                              {it.branch && drill?.branchId === '__total__' && <span className="block text-[9px] text-muted-foreground">{it.branch}</span>}
                            </td>
                            <td className="p-2 text-right tabular-nums font-medium">{fmtSR(it.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Legend */}
        <Card className="border-border bg-muted/20">
          <CardContent className="p-4 text-xs text-muted-foreground space-y-1">
            <div className="flex items-center gap-4 flex-wrap">
              <div><span className="text-info font-semibold">Blue</span> = Revenue</div>
              <div><span className="text-warning font-semibold">Orange</span> = COGS / Expenses</div>
              <div><span className="text-success font-semibold">Green</span> = Profit</div>
              <div><span className="text-error font-semibold">Red</span> = Loss</div>
              <div className="ml-auto"><strong>% Rev</strong> = this row as % of Gross Sales · small % under each cell = share of row total across branches</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
